import React, { useEffect, useState } from 'react';
import { getDept, updateDept } from '../../api/deptApi';
import OrgTreeSelect from '../../components/OrgSelect/OrgTreeSelect';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Stack, 
  Paper, 
  Divider,
  InputAdornment
} from '@mui/material';
import { 
  Edit as EditIcon, 
  AccountTree as AccountTreeIcon,
  Search as SearchIcon,
  Tag as TagIcon,
  Person as PersonIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

function DeptEdit({ code, onCancel, onUpdated  }) {
  const [form, setForm] = useState({
    dept_name: '',
    parent_dept_code: '',
    parent_dept_name: '',
    manager_userid: '',
    sort_order: 0,
  });
  const [popupType, setPopupType] = useState(null); // null | 'dept' | 'user'

  useEffect(() => {
    getDept(code).then(res => {
      const { dept_name, parent_dept_code, parent_dept_name, manager_userid, sort_order } = res.data;
      setForm({ dept_name, parent_dept_code, parent_dept_name, manager_userid, sort_order });
    });
  }, [code]);

  const handleConfirmOrgSelection = (targets) => {
    if (targets.length > 0) {
      const selected = targets[0];
      if (popupType === 'dept') {
        setForm({
          ...form,
          parent_dept_code: selected.target_id,
          parent_dept_name: selected.target_name,
        });
      } else if (popupType === 'user') {
        setForm({
          ...form,
          manager_userid: selected.target_id,
        });
      }
    }
    setPopupType(null);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!form.dept_name.trim()) {
      alert('부서명을 입력하세요.');
      return;
    }
    if (!form.parent_dept_code) {
      const isRoot = window.confirm('상위 부서가 지정되지 않았습니다. 최상위 부서로 변경하시겠습니까?');
      if (!isRoot) return;
    }

    await updateDept(code, form);
    alert('수정 완료');
    if (onUpdated) {
      onUpdated(); 
    } else {
      onCancel();
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e0e4e8', bgcolor: '#fff' }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1.5,
        mb: 3,
        pb: 1.5,
        borderBottom: '2px solid #1976d2' 
      }}>
        <EditIcon sx={{ color: '#1976d2', fontSize: '1.8rem' }} />
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
          부서 정보 수정
        </Typography>
      </Box>

      <Stack spacing={2.5}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057' }}>
            부서 코드 (수정 불가)
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={code}
            disabled
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <TagIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057', display: 'flex', alignItems: 'center' }}>
            부서명 <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
          </Typography>
          <TextField
            fullWidth
            size="small"
            name="dept_name"
            value={form.dept_name}
            onChange={handleChange}
            placeholder="부서명을 입력하세요"
          />
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057' }}>
            상위 부서
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              value={form.parent_dept_name || ''}
              placeholder="상위 부서를 선택하세요"
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountTreeIcon fontSize="small" sx={{ color: '#adb5bd' }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setPopupType('dept')}
              sx={{ whiteSpace: 'nowrap', px: 2 }}
              startIcon={<SearchIcon />}
            >
              선택
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setForm({ ...form, parent_dept_code: '', parent_dept_name: '' })}
              sx={{ whiteSpace: 'nowrap', px: 2 }}
              startIcon={<ClearIcon />}
            >
              삭제
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057' }}>
              정렬 순서
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              name="sort_order"
              value={form.sort_order}
              onChange={handleChange}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057' }}>
              담당자 ID
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                name="manager_userid"
                value={form.manager_userid}
                onChange={handleChange}
                placeholder="관리자를 선택하세요"
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" sx={{ color: '#adb5bd' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setPopupType('user')}
                sx={{ whiteSpace: 'nowrap' }}
                startIcon={<SearchIcon />}
              >
                선택
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setForm({ ...form, manager_userid: '' })}
                sx={{ whiteSpace: 'nowrap' }}
                startIcon={<ClearIcon />}
              >
                삭제
              </Button>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            sx={{ px: 4, fontWeight: 'bold', bgcolor: '#4e73df', '&:hover': { bgcolor: '#2e59d9' } }}
          >
            저장하기
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onCancel}
            sx={{ px: 3 }}
          >
            취소
          </Button>
        </Stack>
      </Stack>

      {popupType && (
        <OrgTreeSelect
          selectionMode="single"
          targetType={popupType}
          initialSelected={
            popupType === 'dept' 
              ? (form.parent_dept_code ? [{ target_type: 'dept', target_id: form.parent_dept_code, target_name: form.parent_dept_name, disp_target_name: form.parent_dept_name }] : [])
              : (form.manager_userid ? [{ target_type: 'user', target_id: form.manager_userid }] : [])
          }
          onConfirm={handleConfirmOrgSelection}
          onCancel={() => setPopupType(null)}
        />
      )}
    </Paper>
  );
}

export default DeptEdit;